import React, { ReactNode, createContext, useContext } from 'react';
import { SplitFactoryProvider, useSplitTreatments } from '@splitsoftware/splitio-react';
import { Logger } from '../../../common/Logger';

// Define props for our provider component
interface CustomSplitProviderProps {
    children: ReactNode;
    businessId?: string | number;
    userId?: string | number;
    userAttributes?: Record<string, any>;
}

// Context for Split configuration and status
interface SplitContextType {
    isReady: boolean;
    businessId?: string | number;
    userId?: string | number;
    isTimedOut: boolean;
    hasError: boolean;
    error?: Error;
    sdkStatus: 'initializing' | 'ready' | 'timeout' | 'error';
}

const SplitContext = createContext<SplitContextType>({
    isReady: false,
    isTimedOut: false,
    hasError: false,
    sdkStatus: 'initializing',
});

/**
 * Enhanced Split client wrapper that provides status information and error handling
 */
const SplitClientWrapper: React.FC<{ children: ReactNode; businessId?: string | number; userId?: string | number }> = ({
    children,
    businessId,
    userId
}) => {
    const [sdkStatus, setSdkStatus] = React.useState<'initializing' | 'ready' | 'timeout' | 'error'>('initializing');
    const [error, setError] = React.useState<Error | undefined>();
    const [isTimedOut, setIsTimedOut] = React.useState(false);

    // Use a dummy treatment to monitor SDK status
    let isReady = false;
    let splitError: Error | null = null;

    try {
        const splitResult = useSplitTreatments({ names: ['_health_check'] });
        isReady = splitResult.isReady;
    } catch (e) {
        splitError = e as Error;
        console.error('[Split.io Context] SDK Error:', e);

        Logger.error({
            error: (e as Error).message,
            stack: (e as Error).stack,
            businessId,
            userId,
            source: 'SplitClientWrapper',
            url: window.location.href,
        }, 'SplitClientWrapper');
    }

    // Handle SDK status changes
    React.useEffect(() => {
        if (splitError) {
            setSdkStatus('error');
            setError(splitError);
            return;
        }

        if (isReady) {
            setSdkStatus('ready');
            setError(undefined);
            setIsTimedOut(false);
            console.log('[Split.io] SDK Ready - Dynamic configuration enabled for business:', businessId);
        }
    }, [isReady, splitError, businessId]);

    // Set up timeout handler
    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (!isReady && !splitError) {
                setIsTimedOut(true);
                setSdkStatus('timeout');
                console.warn('[Split.io] SDK initialization timeout - using default values');

                Logger.error({
                    message: 'Split.io SDK initialization timeout',
                    businessId,
                    userId,
                    source: 'SplitClientWrapper',
                    url: window.location.href,
                }, 'SplitClientWrapper');
            }
        }, 15000); // 15 second timeout

        return () => clearTimeout(timer);
    }, [isReady, splitError, businessId, userId]);

    const splitState: SplitContextType = {
        isReady,
        businessId,
        userId,
        isTimedOut,
        hasError: !!splitError,
        error: splitError || error,
        sdkStatus,
    };

    return (
        <SplitContext.Provider value={splitState}>
            {children}
        </SplitContext.Provider>
    );
};

/**
 * Custom hook to access Split context
 */
export const useSplitContext = (): SplitContextType => {
    const context = useContext(SplitContext);
    if (!context) {
        throw new Error('useSplitContext must be used within a SplitProvider');
    }
    return context;
};

/**
 * Enhanced Split provider with error handling and monitoring
 */
export const SplitProvider: React.FC<CustomSplitProviderProps> = ({
    children,
    businessId,
    userId,
    userAttributes = {}
}) => {
    return (
        <SplitClientWrapper businessId={businessId} userId={userId}>
            {children}
        </SplitClientWrapper>
    );
};

/**
 * Hook to get Split.io SDK health status
 */
export const useSplitHealth = () => {
    const { isReady, isTimedOut, hasError, error, sdkStatus } = useSplitContext();

    return {
        isHealthy: isReady && !hasError && !isTimedOut,
        isReady,
        isTimedOut,
        hasError,
        error,
        sdkStatus,
        canUseFallbacks: !isReady || hasError || isTimedOut,
    };
};